import type { AchievementsState, BossId, PerkId, PlayerProgress, ProgressResponse, RunRecord } from '@cradle7/shared';

const LS_KEY = 'cradle7.runHistory';
const MAX_HISTORY = 20;

/**
 * Client-facing record shape kept stable for UI code (HUD, EndScreen, Stats
 * section, achievements). Mirrors the legacy localStorage record from Phase 0;
 * after hydration the fields are filled from the server's `RunRecord` shape.
 */
export type ClientRunRecord = {
  id: string;
  ts: number;            // createdAt for server runs; Date.now() for offline writes
  outcome: 'won' | 'lost';
  kills: number;
  loot: number;
  perks: PerkId[];
  durationMs: number;
  seed: number;
  score: number;
  /** Phase 2h: present when outcome === 'won' and a boss was defeated on this run. */
  bossDefeated?: BossId;
};

const INITIAL_ACHIEVEMENTS: AchievementsState = {
  bastetDefeated: false,
  fullClearCount: 0,
  reactorEntered: false,
};

let runsCache: ClientRunRecord[] | null = null;
let progressCache: PlayerProgress | null = null;
let achievementsCache: AchievementsState = { ...INITIAL_ACHIEVEMENTS };
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

function safeReadLs(): ClientRunRecord[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ClientRunRecord[]) : [];
  } catch {
    return [];
  }
}

function safeWriteLs(records: ClientRunRecord[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(records));
  } catch {
    /* quota / privacy mode — accept the loss */
  }
}

/** Newest-first array of all locally-known runs. Reads localStorage lazily. */
export function getRuns(): ClientRunRecord[] {
  if (runsCache === null) runsCache = safeReadLs();
  return runsCache;
}

/** Replace the entire run list (used by hydrateFromServer + tests). */
export function setRuns(records: ClientRunRecord[]): void {
  runsCache = records.slice(0, MAX_HISTORY);
  safeWriteLs(runsCache);
  emit();
}

/** Append a single record (newest-first, capped). Used after local + remote submit. */
export function addRun(rec: ClientRunRecord): void {
  const current = getRuns();
  runsCache = [rec, ...current].slice(0, MAX_HISTORY);
  safeWriteLs(runsCache);
  emit();
}

/** Server-side aggregate, or null if we never hydrated. */
export function getProgressSnapshot(): PlayerProgress | null {
  return progressCache;
}

/** Current achievements state. Defaults to all-false until hydrated from server. */
export function getAchievements(): AchievementsState {
  return achievementsCache;
}

/** Replace runs + progress with server snapshot. Server is authoritative. */
export function hydrateFromServer(resp: ProgressResponse): void {
  progressCache = resp.progress;
  runsCache = resp.recentRuns.map(toClientRecord).slice(0, MAX_HISTORY);
  achievementsCache = resp.achievements ? { ...resp.achievements } : { ...INITIAL_ACHIEVEMENTS };
  safeWriteLs(runsCache);
  emit();
}

export function subscribeProgress(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Test hook: clear in-memory caches and listeners without touching localStorage. */
export function clearProgressCache(): void {
  runsCache = null;
  progressCache = null;
  achievementsCache = { ...INITIAL_ACHIEVEMENTS };
  listeners.clear();
}

function toClientRecord(r: RunRecord): ClientRunRecord {
  return {
    id: r.id,
    ts: r.createdAt,
    outcome: r.outcome,
    kills: r.kills,
    loot: r.loot,
    perks: r.perks,
    durationMs: r.durationMs,
    seed: r.seed,
    score: r.score,
    ...(r.bossDefeated ? { bossDefeated: r.bossDefeated } : {}),
  };
}
